import React, { useCallback, useContext, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import { SocketContext, MINUTES_TO_DISPLAY } from "../../context/SocketContext";
import { MediaContext } from "../../context/MediaContext";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const SyncLineChart = () => {
  const { t } = useTranslation("", { keyPrefix: "components.charts" });

  const { graphCache, syncUnderstandings, setGraph, graphVideoTime } = useContext(SocketContext);
  const { player } = useContext(MediaContext);

  const prepareData = () => {
    //Get graph start time
    let startTime = graphVideoTime - MINUTES_TO_DISPLAY * 60;
    if (startTime < 0) startTime = 0;
    //Initialize graph data to an empty array
    const cacheData = [];
    //Get reactions before and after the start time
    const beforeStartReactions = syncUnderstandings.filter((r) => r.videoTs <= startTime);
    const afterStartReactions = syncUnderstandings.filter((r) => r.videoTs > startTime);
    //Const get the last reaction given before the start time
    if (beforeStartReactions[beforeStartReactions.length - 1]) {
      cacheData.push(beforeStartReactions[beforeStartReactions.length - 1].percentage * 100);
    } else {
      cacheData.push(undefined);
    }
    //Fill the other 299 values
    for (let second = 1; second < MINUTES_TO_DISPLAY * 60; second++) {
      //Take data in the given second
      const tmpReactions = afterStartReactions.filter((r) => parseInt(r.videoTs) === startTime + second);
      if (tmpReactions.length > 0) {
        //If there is data in that second
        if (tmpReactions[0].percentage !== undefined && tmpReactions[0].percentage !== null) {
          cacheData.push(tmpReactions[0].percentage * 100);
        } else {
          cacheData.push(undefined);
        }
      } else {
        //Else push the previous value
        cacheData.push(cacheData[second - 1]);
      }
    }
    return cacheData;
  };

  useEffect(() => {
    const preparedData = prepareData();
    setGraph(preparedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getColor = () => {
    const lastPerc = graphCache[graphCache.length - 1];
    if (lastPerc >= 80) return "75, 192, 192";
    if (lastPerc >= 50) return "255, 205, 86";
    else if (lastPerc >= 0) return "255, 99, 132";
    return "79, 103, 115";
  };

  const options = {
    maintainAspectRatio: false,
    animation: false,
    scales: {
      y: {
        max: 105,
        min: 0,
        ticks: {
          callback: function (value, index, values) {
            return value > 100 ? "" : value < 0 ? "" : value + "%";
          },
        },
        grid: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: t("last_minutes"),
        position: "top",
        padding: 0,
      },
    },
    interaction: {
      mode: "point",
    },
    onClick: (e, element) => {
      if (element.length > 0) {
        var ind = element[0].index;
        var point = data.datasets[0].data[ind]; //point.x = videoSeconds
        player.seekTo(point.x);
      }
    },
  };

  const data = {
    labels: new Array(graphCache.length).fill(""),
    datasets: [
      {
        label: t("percentage"),
        borderColor: `rgba(${getColor()},1)`,
        borderWidth: 7,
        fill: true,
        pointRadius: 1,
        data: graphCache,
        backgroundColor: `rgba(${getColor()},0.5)`,
        borderDash: [],
        borderDashOffset: 1.0,
        borderJoinStyle: "miter",
        pointHitRadius: 1,
      },
    ],
  };

  return <Line data={data} options={options} />;
};

export default SyncLineChart;
